import {
  ForwardedRef,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import cn from 'classnames';

import Heading from '@components/ui/Heading';
import { useLocale } from '@hooks/useLocale';
import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { addLocaleToUrl } from '@utils/url';
import {
  ABSOLUTE_LINK_PATTERN,
  LOCALE_PREFIX_PATTERN,
} from '@utils/breadcrumbs/withLocalePrefix';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackHotspotCtaClick } from '_utils/GA4/trackHotspotEvents';
import type { ImageHotspotCta } from '@adapters/prismic/transforms/imageHotspot';

import styles from './ReviewPanel.module.scss';

/** How long the mobile trigger's "Staff advice" label stays peeked out before
    re-collapsing to the icon-only face, once it's scrolled into view (Figma's
    animation spec times the expand/collapse transitions themselves — 600ms
    open, 400ms close — but doesn't state a hold duration, so this is a
    judgment call, matching QuickAdd's own success-message dwell). */
const PEEK_HOLD_MS = 2500;

export interface ReviewPanelProps {
  heading: string | null;
  /** `ImageHotspot`'s optional top-of-section heading (`ImageHotspotProps.heading`)
      renders as an h2 when present, making this tab heading its child — h3
      in that case, h2 (default, unchanged) when there's no section heading
      above it. Visual style is unaffected — `styleLevel` below stays "2"
      either way. */
  headingSemanticLevel?: 'h2' | 'h3';
  quote: string | null;
  quoteAttributionLine1: string | null;
  quoteAttributionLine2: string | null;
  /** Overrides the `imageHotspot.staffPicksEyebrow` i18n default when a
      merchandiser has authored one (ED-345 QA #11) — `null`/omitted falls
      back to the translated default. */
  quoteEyebrowLabel?: string | null;
  /** Overrides the `imageHotspot.staffAdviceLabel` i18n default when a
      merchandiser has authored one (ED-345 QA #12) — same fallback. */
  quoteTriggerLabel?: string | null;
  dynamicPeek: boolean;
  ctas: ImageHotspotCta[];
  /** The active tab's own label/co-op brand — for `click_hotspot_cta`
      (ED-416 (4)), which this component fires itself since it already owns
      the CTA links. */
  tabName: string;
  coOpBrand: string | null;
  /** `ImageHotspot` measures the desktop quote panel (via the forwarded
      ref below) against the active hotspot dots/tab bar and sets this when
      they'd overlap — falls back to the mobile collapsed-trigger/reveal-card
      treatment even at desktop viewport widths instead of the always-visible
      bottom-left panel silently covering (and swallowing clicks on)
      whatever's underneath it. See `quoteOverlap.ts`. Defaults to `false`. */
  preferCompactQuote?: boolean;
}

/**
 * Mirrors `_components/MainHero/index.tsx`'s `localizeHref`: absolute links
 * (`https:`, `mailto:`, `tel:`, protocol-relative `//`) and fragments are left
 * untouched, and a url that already carries a locale segment (e.g. `/en/camping`)
 * isn't re-prefixed — only a bare relative path gets `addLocaleToUrl`'d.
 */
const buildCtaHref = (raw: string, locale: string): string => {
  if (ABSOLUTE_LINK_PATTERN.test(raw)) return raw;
  const [path] = raw.split(/[?#]/);
  return LOCALE_PREFIX_PATTERN.test(path) ? raw : addLocaleToUrl(raw, locale);
};

/** Up to two stacked attribution lines below the quote (e.g. author, then
    location) — either may be authored independently. Grouped under one
    wrapper so the two lines sit flush together as a single flex item; left
    as separate `<p>`s (rather than one `<p>` with a `<br>`) so each line
    stays its own text node for both styling and testing. Rendering this as
    two directly-adjacent flex children of `.desktopQuotePanel`/
    `.mobileQuoteCard` would otherwise pick up that parent's own
    inter-child `gap`, prying the lines apart. */
const QuoteAttribution = ({
  line1,
  line2,
}: {
  line1: string | null;
  line2: string | null;
}): ReactElement | null =>
  !line1 && !line2 ? null : (
    <div className={styles.quoteAttribution}>
      {line1 && <p>{line1}</p>}
      {line2 && <p>{line2}</p>}
    </div>
  );

const CtaLinks = ({
  ctas,
  locale,
  className,
  interactionLocation,
  tabName,
  coOpBrand,
}: {
  ctas: ImageHotspotCta[];
  locale: string;
  className?: string;
  interactionLocation: string;
  tabName: string;
  coOpBrand: string | null;
}): ReactElement | null =>
  ctas.length === 0 ? null : (
    <div className={cn(styles.ctaGroup, className)}>
      {ctas.map((cta, index) => {
        const href = buildCtaHref(cta.url, locale);

        // ED-416 (4): a real `<a href>` navigation (like
        // `PlpCrossLinkingCard`'s own cards), so a modified click (opens in a
        // new tab) keeps this page alive and fires normally, while a plain
        // click defers navigation until the hit is sent (or GTM times out).
        const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
          const data = {
            interactionLocation,
            linkUrl: href,
            linkText: cta.label,
            tabName,
            coOpBrand,
            index,
          };

          if (isModifiedClick(event)) {
            trackHotspotCtaClick({ data });
            return;
          }

          event.preventDefault();
          trackHotspotCtaClick({
            data,
            eventCallback: () => {
              window.location.href = href;
            },
          });
        };

        return (
          <a
            // `cta.url` alone isn't guaranteed unique — two CTAs in the same
            // tab could plausibly point at the same URL (e.g. both linking
            // to the same collection). Combined with `index` instead.
            // eslint-disable-next-line react/no-array-index-key -- `index` is combined with `cta.url` above, not used bare, so it stays unique even when two CTAs do share a URL.
            key={`${cta.url}-${index}`}
            href={href}
            onClick={handleClick}
            className={styles.ctaLink}
          >
            {cta.label}
            <span className={styles.ctaUnderline} aria-hidden="true" />
          </a>
        );
      })}
    </div>
  );

/**
 * The tab's footer content over the image: either the staff-quote panel
 * (when `dynamicPeek` is on and a quote is authored) or a plain
 * heading + CTAs state — Figma's "Dynamic Peek=FALSE" frame. On mobile the
 * quote is always collapsed behind an icon by default (regardless of
 * `dynamicPeek`, which only governs desktop's auto-expand-on-load), with
 * every CTA shown bottom-left beside that icon from the start — never
 * inside the expanded quote card (ED-533; supersedes ED-345's original
 * one-CTA cap at Digital Merchandising's request).
 */
const ReviewPanel = forwardRef(function ReviewPanel(
  {
    heading,
    headingSemanticLevel = 'h2',
    quote,
    quoteAttributionLine1,
    quoteAttributionLine2,
    quoteEyebrowLabel = null,
    quoteTriggerLabel = null,
    dynamicPeek,
    ctas,
    tabName,
    coOpBrand,
    preferCompactQuote = false,
  }: ReviewPanelProps,
  desktopQuotePanelRef: ForwardedRef<HTMLDivElement>
): ReactElement | null {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const { asPath } = useRouter();
  const interactionLocation = asPath.split('?')[0];
  const prefersReducedMotion = usePrefersReducedMotion();
  const [mobileQuoteOpen, setMobileQuoteOpen] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const isAnchorVisible = useObjectVisibility(anchorRef, 0.5, true);

  // "When the user reaches this section while scrolling, the Expert Advice
  // button automatically expands to display the 'Staff Advice' label" (Figma
  // animation spec) — peeks once, then re-collapses to the icon-only face
  // after PEEK_HOLD_MS, unless the shopper opens the quote first.
  useEffect(() => {
    if (!isAnchorVisible || mobileQuoteOpen) return undefined;
    setPeeking(true);
    const timer = window.setTimeout(() => setPeeking(false), PEEK_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [isAnchorVisible, mobileQuoteOpen]);

  // ED-345 QA #10: dismissible by the X button (its own onClick below) OR by
  // tapping/clicking anywhere outside the card — mirrors `AutoComplete`'s own
  // outside-press pattern. A press on the trigger button itself is still
  // inside `anchorRef` (it wraps both the card and the button), so this never
  // double-toggles against the button's own onClick.
  useEffect(() => {
    if (!mobileQuoteOpen) return undefined;
    const handleDocumentPointerDown = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) {
        setMobileQuoteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentPointerDown);
    return () =>
      document.removeEventListener('mousedown', handleDocumentPointerDown);
  }, [mobileQuoteOpen]);

  const showQuote = dynamicPeek && !!quote;

  if (!showQuote) {
    return (
      <div className={styles.headingPanel}>
        {heading && (
          <Heading
            semanticLevel={headingSemanticLevel}
            styleLevel="2"
            className={styles.heading}
          >
            {heading}
          </Heading>
        )}
        <CtaLinks
          ctas={ctas}
          locale={locale}
          interactionLocation={interactionLocation}
          tabName={tabName}
          coOpBrand={coOpBrand}
        />
      </div>
    );
  }

  // ED-345 QA #11: merchandisers can override this away from the default —
  // it's authored copy, not necessarily "staff picks".
  const eyebrow = quoteEyebrowLabel || t('imageHotspot.staffPicksEyebrow');

  const desktopQuoteBody = (
    <>
      <div className={styles.reviewText}>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <div className={styles.quoteRow}>
          <span className={styles.mecPeakAvatar} aria-hidden="true">
            <Image
              src="/images/mec-peak-icon.svg"
              width={16}
              height={16}
              alt=""
            />
          </span>
          <blockquote className={styles.quote}>“{quote}”</blockquote>
        </div>
        <QuoteAttribution
          line1={quoteAttributionLine1}
          line2={quoteAttributionLine2}
        />
      </div>
      <CtaLinks
        ctas={ctas}
        locale={locale}
        interactionLocation={interactionLocation}
        tabName={tabName}
        coOpBrand={coOpBrand}
      />
    </>
  );

  // No CTAs in here — they sit in `.mobileFooter` below, visible without
  // opening the card (Figma 427:4372; ED-533).
  const mobileQuoteBody = (
    <>
      <p className={styles.eyebrow}>{eyebrow}</p>
      <blockquote className={styles.quote}>“{quote}”</blockquote>
      <QuoteAttribution
        line1={quoteAttributionLine1}
        line2={quoteAttributionLine2}
      />
    </>
  );

  const mobileQuoteCard = mobileQuoteOpen && (
    <div className={styles.mobileQuoteCard}>{mobileQuoteBody}</div>
  );

  return (
    <div
      className={styles.quotePanelRoot}
      data-compact-preferred={preferCompactQuote || undefined}
    >
      <div className={styles.desktopQuotePanel} ref={desktopQuotePanelRef}>
        {desktopQuoteBody}
      </div>

      {/* Bottom row shared by the CTAs (left) and the quote trigger (right)
          — one flex row, so the trigger's peek-state widening squeezes the
          CTAs instead of painting over them (ED-533). */}
      <div className={styles.mobileFooter}>
        <CtaLinks
          ctas={ctas}
          locale={locale}
          className={styles.mobileFooterCtas}
          interactionLocation={interactionLocation}
          tabName={tabName}
          coOpBrand={coOpBrand}
        />

        <div className={styles.mobileQuoteAnchor} ref={anchorRef}>
          {prefersReducedMotion ? (
            mobileQuoteCard
          ) : (
            <AnimatePresence>
              {mobileQuoteOpen && (
                <motion.div
                  className={styles.mobileQuoteCard}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
                  exit={{ opacity: 0, y: 16, transition: { duration: 0.4 } }}
                >
                  {mobileQuoteBody}
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* One toggle button for both states — rather than swapping in a
            differently-typed button on open/close — so a keyboard/screen
            reader user's focus stays put across the transition instead of
            falling back to `document.body`. */}
          <button
            type="button"
            className={cn(styles.mobileQuoteTrigger, {
              [styles.mobileQuoteTriggerPeeking]: !mobileQuoteOpen && peeking,
            })}
            aria-expanded={mobileQuoteOpen}
            aria-label={
              mobileQuoteOpen
                ? t('close')
                : t('imageHotspot.expandQuoteAriaLabel')
            }
            onClick={() => {
              if (mobileQuoteOpen) {
                setMobileQuoteOpen(false);
                return;
              }
              setMobileQuoteOpen(true);
              setPeeking(false);
            }}
          >
            {mobileQuoteOpen ? (
              <XIcon size={16} aria-hidden weight="regular" />
            ) : (
              <>
                <Image
                  src="/images/mec-peak-icon.svg"
                  width={16}
                  height={16}
                  alt=""
                  className={styles.mecPeakIcon}
                />
                {peeking && (
                  <span className={styles.mobileQuoteTriggerLabel}>
                    {/* ED-345 QA #12: merchandiser-authored override, same
                      fallback pattern as `eyebrow` above. */}
                    {quoteTriggerLabel || t('imageHotspot.staffAdviceLabel')}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ReviewPanel;
